import {
  ICenterLineRowConflict,
  centerLineRowValidatorFn,
} from "../baysData/centerLineRowValidator";
import { cloneObject, createDictionary } from "@tedivo/tedivo-pure-helpers";

import { IOpenVesselDefinitionV1 } from "open-vessel-definition";

export interface IFixCenterLineRowResult {
  /** The vessel without the contradictions. The input object when nothing changed */
  json: IOpenVesselDefinitionV1;
  /** The conflicts repaired, each saying how */
  repaired: ICenterLineRowConflict[];
  changed: boolean;
}

/**
 * Repairs what `centerLineRowValidator` reports: Bay-levels holding row 00 slots
 * although they have no centerline row. The input is not modified.
 *
 * - `clearOptOut`: the Bay-level's `centerLineRow: 0` is removed, so it follows the
 *   vessel-wide flag again and its row 00 slots become valid.
 * - `dropSlots`: the row 00 slots are removed, along with the row's `perRowInfo`
 *   entry. When the vessel has no centerline row, the Bay-level flag is removed too,
 *   as the per-bay toggle must be unset in that case.
 */
export function fixCenterLineRow(json: IOpenVesselDefinitionV1): IFixCenterLineRowResult {
  const validation = centerLineRowValidatorFn({
    data: { json, lcgVcgTcgAndPairings: undefined, lcgsBy20Bay: undefined },
  });

  const conflicts = validation.result.conflicts;

  if (conflicts.length === 0) return { json, repaired: [], changed: false };

  const newJson = cloneObject(json);
  const baysDict = createDictionary(newJson.baysData, (bl) => `${bl.isoBay}-${bl.level}`);
  const vesselHasRow00 = !!newJson.sizeSummary?.centerLineRow;

  conflicts.forEach((conflict) => {
    const bl = baysDict[`${conflict.isoBay}-${conflict.level}`];
    if (bl === undefined) return;

    if (conflict.repair === "clearOptOut") {
      delete bl.centerLineRow;
      return;
    }

    conflict.slots.forEach((pos) => {
      if (bl.perSlotInfo) delete bl.perSlotInfo[pos];
    });

    if (bl.perRowInfo?.each) delete bl.perRowInfo.each["00"];

    if (!vesselHasRow00) delete bl.centerLineRow;
  });

  return { json: newJson, repaired: conflicts, changed: true };
}
